import React from "react";
import Button from "../../../components/ui/Button";
import type { User } from "../../../types";

interface ViewUserDetailsModalProps {
	isOpen: boolean;
	onClose: () => void;
	user: User | null;
	onEdit?: () => void;
}

const ViewUserDetailsModal: React.FC<ViewUserDetailsModalProps> = ({
	isOpen,
	onClose,
	user,
	onEdit,
}) => {
	if (!isOpen || !user) return null;

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border sticky top-0 bg-card z-10">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								User Details
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								View complete user information
							</p>
						</div>
						<div className="flex items-center space-x-2">
							{onEdit && (
								<Button
									variant="outline"
									size="sm"
									onClick={onEdit}
									iconName="Edit"
									iconPosition="left"
								>
									Edit
								</Button>
							)}
							<Button
								variant="ghost"
								size="icon"
								onClick={onClose}
								iconName="X"
							/>
						</div>
					</div>
				</div>

				{/* Content */}
				<div className="p-6 space-y-6">
					{/* Basic Information */}
					<div>
						<h3 className="text-lg font-medium text-foreground mb-4">
							Basic Information
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Full Name
								</label>
								<p className="text-foreground mt-1">
									{user.first_name} {user.last_name}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Email Address
								</label>
								<p className="text-foreground mt-1">{user.email}</p>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Phone Number
								</label>
								<p className="text-foreground mt-1">
									{user.phone || "Not provided"}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Role
								</label>
								<p className="text-foreground mt-1">
									{user.role === "tenant" && user.property_owner ? (
										<span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
											Property Owner
										</span>
									) : user.role === "tenant" ? (
										<span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
											Tenant
										</span>
									) : (
										<span className="capitalize">{user.role}</span>
									)}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Account Status
								</label>
								<div className="flex items-center space-x-2 mt-1">
									<div
										className={`w-2 h-2 rounded-full ${
											user.is_active ? "bg-success" : "bg-error"
										}`}
									/>
									<p className="text-foreground">
										{user.is_active ? "Active" : "Inactive"}
									</p>
								</div>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Member Since
								</label>
								<p className="text-foreground mt-1">
									{formatDate(user.created_at)}
								</p>
							</div>
						</div>
					</div>

					{/* Role-Specific Information */}
					{user.role === "tenant" && (
						<div>
							<h3 className="text-lg font-medium text-foreground mb-4">
								Tenant Information
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{user.unit_number && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Unit Number
										</label>
										<p className="text-foreground mt-1">{user.unit_number}</p>
									</div>
								)}

								{user.property_address && (
									<div className="md:col-span-2">
										<label className="text-sm font-medium text-muted-foreground">
											Property Address
										</label>
										<p className="text-foreground mt-1">
											{user.property_address}
										</p>
									</div>
								)}

								{user.emergency_contact_name && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Emergency Contact Name
										</label>
										<p className="text-foreground mt-1">
											{user.emergency_contact_name}
										</p>
									</div>
								)}

								{user.emergency_contact_phone && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Emergency Contact Phone
										</label>
										<p className="text-foreground mt-1">
											{user.emergency_contact_phone}
										</p>
									</div>
								)}

								{user.emergency_contact_relation && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Emergency Contact Relation
										</label>
										<p className="text-foreground mt-1">
											{user.emergency_contact_relation}
										</p>
									</div>
								)}

								{user.lease_code && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Lease Code
										</label>
										<p className="text-foreground mt-1">{user.lease_code}</p>
									</div>
								)}
							</div>
						</div>
					)}

					{user.role === "technician" && user.primary_specialty && (
						<div>
							<h3 className="text-lg font-medium text-foreground mb-4">
								Technician Information
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Primary Specialty
									</label>
									<p className="text-foreground mt-1">
										{user.primary_specialty}
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Account Metadata */}
					<div className="pt-6 border-t border-border">
						<h3 className="text-lg font-medium text-foreground mb-4">
							Account Metadata
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="text-sm font-medium text-muted-foreground">
									User ID
								</label>
								<p className="text-foreground mt-1 font-mono text-xs">
									{user.id}
								</p>
							</div>

							<div>
								<label className="text-sm font-medium text-muted-foreground">
									Last Updated
								</label>
								<p className="text-foreground mt-1">
									{formatDate(user.updated_at)}
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="p-6 border-t border-border flex items-center justify-end space-x-3">
					<Button variant="outline" onClick={onClose}>
						Close
					</Button>
					{onEdit && (
						<Button onClick={onEdit} iconName="Edit" iconPosition="left">
							Edit User
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ViewUserDetailsModal;
